import type { QueryClient, UseQueryOptions } from '@w/react-query'
import type {
  ClientErrorGroup,
  DeltaMetricPoint,
  GaugeMetricPoint,
  LogData,
  MetricKind,
  MetricTemporalityKind,
  SpanData,
  TraceData,
} from '@w/tel'
import { getAll, getAllFromIndex, getOne, lowerBound } from './idb'

export const timeFilters = [
  '15min',
  '1h',
  '3h',
  '1d',
  '3d',
  '7d',
  '14d',
] as const
export type TimeFilter = (typeof timeFilters)[number]
export interface SpanTableFilters {
  target?: NonNullable<SpanData['target']>
  status?: NonNullable<SpanData['status']>
  resourceName?: string
  timeFrom?: TimeFilter
  /** Trimmed; ASCII-case-insensitive substring of name/displayName, or exact id/traceId. */
  q?: string
  /** 'time' (default): newest ingested first. 'duration': longest first, unfinished last. */
  sort?: 'time' | 'duration'
}
export type SpanRecord = SpanData & {
  resourceName?: string
  resourceEnv?: string
  resourceVersion?: string
  ingestedAt: number
  /** Derived on trace-list roots: another span of the trace has status error. Root status stays raw. */
  hasFailedDescendant?: boolean
}
export type LogRecord = LogData & {
  resourceName?: string
  resourceEnv?: string
  resourceVersion?: string
  ingestedAt: number
}
export type ErrorGroupRecord = ClientErrorGroup & {
  userIds: string[]
  companyIds: string[]
  evidence: unknown
  trace?: TraceData
}
export type MetricRecord = (DeltaMetricPoint | GaugeMetricPoint) & {
  id: string
  name: string
  kind: MetricKind
  temporality?: MetricTemporalityKind
  resourceName: string
  resourceEnv: string
  ingestedAt: number
}

export interface RemoteTraceRecord {
  traceId: string
  url: string
  at: number
}

export interface TelReadDb {
  spans: {
    listOptions(
      filters: SpanTableFilters & { limit?: number },
    ): UseQueryOptions<SpanRecord[]>
  }
  traces: {
    getOptions(traceOrSpanId: string): UseQueryOptions<TraceData | null>
    remoteOptions(
      traceId: string,
    ): UseQueryOptions<RemoteTraceRecord | undefined>
  }
  errorGroups: { listOptions(): UseQueryOptions<ErrorGroupRecord[]> }
  metrics: {
    listOptions(range: { fromMs: number }): UseQueryOptions<MetricRecord[]>
  }
  invalidateAll(queryClient: QueryClient, opts?: { type?: 'active' }): void
}

export function createTelReadDb(opts: { dbName?: string } = {}): TelReadDb {
  const dbName = opts.dbName ?? 'tel'
  return {
    spans: {
      listOptions: (filters) => ({
        queryKey: ['tel', dbName, 'spans', filters],
        queryFn: async () => {
          const from = filters.timeFrom
            ? Date.now() - timeFilterMs(filters.timeFrom)
            : 0
          const [spans, errorSpans] = await Promise.all([
            getAllFromIndex<SpanRecord>(
              dbName,
              'spans',
              'ingestedAt',
              lowerBound(from),
            ),
            // Not time-bounded: a child ends and is ingested before its root, so it can precede the window.
            getAllFromIndex<SpanRecord>(
              dbName,
              'spans',
              'status',
              IDBKeyRange.only('error'),
            ),
          ])
          const failedSpanIdsByTrace = new Map<string, Set<string>>()
          for (const span of errorSpans) {
            if (span.traceId === undefined) continue
            const ids = failedSpanIdsByTrace.get(span.traceId) ?? new Set()
            ids.add(span.id)
            failedSpanIdsByTrace.set(span.traceId, ids)
          }
          const q = filters.q?.trim()
          return spans
            .filter(
              (span) =>
                (!span.parentId || span.id === span.traceId) &&
                (!q || spanMatchesSearch(span, q)) &&
                (!filters.target || span.target === filters.target) &&
                (!filters.resourceName ||
                  span.resourceName === filters.resourceName),
            )
            .map((span) => {
              const failed =
                span.traceId === undefined
                  ? undefined
                  : failedSpanIdsByTrace.get(span.traceId)
              // Spans are fresh copies read from IndexedDB, so mutating them is local.
              return Object.assign(span, {
                hasFailedDescendant:
                  failed !== undefined &&
                  (failed.size > 1 || !failed.has(span.id)),
              })
            })
            .filter(
              (span) =>
                !filters.status ||
                span.status === filters.status ||
                (filters.status === 'error' && span.hasFailedDescendant),
            )
            .toSorted((left, right) =>
              (filters.sort === 'duration' ? _byDurationDesc(left, right) : 0) ||
              // Code-unit order, as SQLite `ORDER BY id DESC` on the server; never locale order.
              right.ingestedAt - left.ingestedAt || (right.id < left.id ? -1 : right.id > left.id ? 1 : 0),
            )
            .slice(0, filters.limit)
        },
      }),
    },
    traces: {
      getOptions: (id) => ({
        queryKey: ['tel', dbName, 'traces', id],
        queryFn: async () => {
          const all = await getAll<SpanRecord>(dbName, 'spans')
          const selected = all.find((span) => span.id === id)
          if (!selected) return null
          const traceId = selected.traceId ?? selected.id
          const spans = all.filter(
            (span) => (span.traceId ?? span.id) === traceId,
          )
          const logs = (await getAll<LogRecord>(dbName, 'logs')).filter(
            (log) => log.traceId === traceId,
          )
          const byId = new Map<string, TraceData>()
          for (const span of spans)
            byId.set(span.id, { ...span, logs: [], children: [] })
          for (const log of logs) byId.get(log.parentId ?? '')?.logs.push(log)
          for (const span of spans) {
            const node = byId.get(span.id)
            const parent = span.parentId ? byId.get(span.parentId) : undefined
            if (node && parent) parent.children.push(node)
          }
          for (const node of byId.values()) {
            node.logs.sort((a, b) => a.id.localeCompare(b.id))
            // Span timestamps mark completion; ongoing spans follow known start times.
            node.children.sort((a, b) =>
              ((a.timestamp ?? Infinity) - (a.duration ?? 0)) - ((b.timestamp ?? Infinity) - (b.duration ?? 0)) || a.id.localeCompare(b.id))
          }
          return (
            byId.get(traceId) ??
            byId.get(spans.find((span) => !span.parentId)?.id ?? '') ??
            null
          )
        },
      }),
      remoteOptions: (traceId) => ({
        queryKey: ['tel', dbName, 'remoteTraces', traceId],
        queryFn: () =>
          getOne<RemoteTraceRecord>(dbName, 'remoteTraces', traceId),
      }),
    },
    errorGroups: {
      listOptions: () => ({
        queryKey: ['tel', dbName, 'errorGroups'],
        queryFn: () => getAll<ErrorGroupRecord>(dbName, 'errorGroups'),
      }),
    },
    metrics: {
      listOptions: (range) => ({
        queryKey: ['tel', dbName, 'metrics', range],
        queryFn: () =>
          getAllFromIndex<MetricRecord>(
            dbName,
            'metrics',
            'ingestedAt',
            lowerBound(range.fromMs),
          ),
      }),
    },
    invalidateAll(queryClient, opts) {
      void queryClient.invalidateQueries({
        queryKey: ['tel', dbName],
        ...(opts?.type ? { type: opts.type } : {}),
      })
    },
  }
}

/** Same contract as tel-mini-be `/spans?q=` (SQLite `lower` folds ASCII only). */
export function spanMatchesSearch(span: Pick<SpanRecord, 'id' | 'traceId' | 'name' | 'displayName'>, q: string): boolean {
  const needle = _asciiLower(q)
  return span.id === q || span.traceId === q ||
    _asciiLower(span.name).includes(needle) ||
    (span.displayName !== undefined && _asciiLower(span.displayName).includes(needle))
}

function _asciiLower(value: string): string {
  return value.replaceAll(/[A-Z]/g, char => char.toLowerCase())
}

function _byDurationDesc(left: SpanRecord, right: SpanRecord): number {
  if (left.duration === undefined || right.duration === undefined)
    return Number(left.duration === undefined) - Number(right.duration === undefined)
  return right.duration - left.duration
}

export function timeFilterMs(value: TimeFilter): number {
  const values: Record<TimeFilter, number> = {
    '15min': 900_000,
    '1h': 3_600_000,
    '3h': 10_800_000,
    '1d': 86_400_000,
    '3d': 259_200_000,
    '7d': 604_800_000,
    '14d': 1_209_600_000,
  }
  return values[value]
}
